/**
 * Business Environment Factor Create Route
 *
 * Create a new business environment factor using BusinessEnvironmentFactorForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { BusinessEnvironmentFactorForm } from '../../components/business-environment-factors';
import {
  createBusinessEnvironmentFactor,
  type CreateBusinessEnvironmentFactorInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessEnvironmentFactorFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/business-environment-factors/new')({
  component: BusinessEnvironmentFactorCreatePage,
});

function BusinessEnvironmentFactorCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateBusinessEnvironmentFactorFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateBusinessEnvironmentFactorInput = {
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category || undefined,
        label: formData.label || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      const newBusinessEnvironmentFactor = await createBusinessEnvironmentFactor(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessEnvironmentFactors'],
      });

      // Navigate to the new business environment factor detail page
      navigate({
        to: `/business-environment-factors/${newBusinessEnvironmentFactor.entityId}` as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="business-environment-factor-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Business Environment Factor</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new business environment factor
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessEnvironmentFactorForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
